// =============================================================================
// SimSense Platform — Corner Analysis Page
// =============================================================================

import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { CornerLossBar } from "../components/telemetry/CornerLossBar";
import { api } from "../utils/api";
import { useUnits } from "../hooks/useUnits";
import { Select } from "../components/Select";

export function CornerAnalysis() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const units = useUnits();
  const [session, setSession] = useState<any>(null);
  const [manifest, setManifest] = useState<any>(null);
  const [comparison, setComparison] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const lapA = Number(searchParams.get("lapA") || 0);
  const lapB = Number(searchParams.get("lapB") || 0);

  useEffect(() => {
    async function fetchManifest() {
      try {
        const [sData, mData] = await Promise.all([
          api<any>(`/api/sessions/${id}`),
          api<any>(`/api/sessions/${id}/lap-manifest`),
        ]);
        setSession(sData);
        setManifest(mData);
        document.title = `Corner Analysis — ${sData.trackName || "Session"} | SimSense`;
        // Default selection if none in URL
        if (!searchParams.get("lapA") && mData.laps.length > 0) {
          const validLaps = mData.laps.filter((l: any) => l.isValid && l.lapTime > 0 && l.lapNum > 0);
          if (validLaps.length >= 2) {
            setSearchParams({ lapA: String(validLaps[0].globalLap), lapB: String(validLaps[1].globalLap) });
          }
        }
      } catch (err) {
        console.error("Failed to fetch manifest", err);
      }
    }
    fetchManifest();
  }, [id]);

  useEffect(() => {
    async function fetchComparison() {
      if (!lapA || !lapB) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await api<any[]>(`/api/sessions/${id}/compare-corners?lapA=${lapA}&lapB=${lapB}`);
        setComparison(data || []);
      } catch (err) {
        console.error("Failed to fetch corner comparison", err);
        setComparison([]);
      } finally {
        setLoading(false);
      }
    }
    fetchComparison();
  }, [id, lapA, lapB]);

  if (!session || !manifest) return <div style={{ padding: "40px", textAlign: "center" }}>Loading session...</div>;

  return (
    <div>
      {/* Breadcrumbs */}
      <div style={{ marginBottom: "16px", fontSize: "13px", color: "var(--text2)" }}>
        <Link to="/" style={{ color: "var(--accent)", textDecoration: "none" }}>Sessions</Link>
        {" / "}
        <Link to={`/session/${id}`} style={{ color: "var(--accent)", textDecoration: "none" }}>{session.trackName}</Link>
        {" / "}
        <span>Corner Analysis</span>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h2>Corner-by-Corner Analysis</h2>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <label style={{ fontSize: "12px", color: "var(--text2)" }}>Compare Lap:</label>
          <Select
            value={String(lapA)}
            onChange={v => setSearchParams({ lapA: v, lapB: String(lapB) })}
            options={manifest.laps.filter((l: any) => l.isValid && l.lapNum > 0).map((l: any) => ({ value: String(l.globalLap), label: `Lap ${l.lapNum}` }))}
            size="sm"
          />
          <span style={{ color: "var(--text2)" }}>vs</span>
          <Select
            value={String(lapB)}
            onChange={v => setSearchParams({ lapA: String(lapA), lapB: v })}
            options={manifest.laps.filter((l: any) => l.isValid && l.lapNum > 0).map((l: any) => ({ value: String(l.globalLap), label: `Lap ${l.lapNum}` }))}
            size="sm"
          />
        </div>
      </div>

      <CornerLossBar comparison={comparison} />

      <div style={{ background: "var(--card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", background: "rgba(255,255,255,0.02)" }}>
              <th style={{ padding: "12px 16px" }}>Corner</th>
              <th style={{ padding: "12px 16px" }}>Metric</th>
              <th style={{ padding: "12px 16px", color: "#3b82f6" }}>Lap {manifest.laps.find((l: any) => l.globalLap === lapA)?.lapNum}</th>
              <th style={{ padding: "12px 16px", color: "#ef4444" }}>Lap {manifest.laps.find((l: any) => l.globalLap === lapB)?.lapNum}</th>
              <th style={{ padding: "12px 16px" }}>Delta</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center" }}>Loading corner data...</td></tr>
            ) : comparison.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center" }}>No corner data found for these laps.</td></tr>
            ) : (
              comparison.map((c, i) => (
                <CornerRow key={i} corner={c} units={units} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CornerRow({ corner, units }: { corner: any, units: any }) {
  const metrics = [
    { 
      key: "timeLossMs", 
      label: "Time Loss", 
      format: (v: number) => units.formatDelta(v / 1000),
      note: "Total time lost/gained in this corner compared to the reference lap."
    },
    { 
      key: "entrySpeed", 
      label: "Entry Speed", 
      format: (v: number) => units.formatSpeed(v),
      note: "Average speed during the first 25% of the corner sequence."
    },
    { 
      key: "minSpeed", 
      label: "Apex Speed", 
      format: (v: number) => units.formatSpeed(v),
      note: "The lowest speed achieved within the corner boundaries."
    },
    { 
      key: "exitSpeed", 
      label: "Exit Speed", 
      format: (v: number) => units.formatSpeed(v),
      note: "Average speed during the last 25% of the corner sequence."
    },
    { 
      key: "peakBrake", 
      label: "Peak Brake", 
      format: (v: number) => (v * 100).toFixed(0) + "%",
      note: "Maximum brake pressure applied within the corner."
    },
    { 
      key: "brakeOnsetPct", 
      label: "Brake Onset", 
      format: (v: number) => (v * 100).toFixed(1) + "%",
      note: "Point where braking exceeds 5% pressure (0-100% of lap distance)."
    },
    { 
      key: "brakeReleasePct", 
      label: "Brake Release", 
      format: (v: number) => (v * 100).toFixed(1) + "%",
      note: "Final point where brakes drop below 5% pressure."
    },
    { 
      key: "throttleOnPct", 
      label: "Throttle On", 
      format: (v: number) => (v * 100).toFixed(1) + "%",
      note: "Point after apex where throttle application begins (10%+)."
    },
    { 
      key: "throttleRollDist", 
      label: "Throttle Roll-on", 
      format: (v: number) => (v * 100).toFixed(1) + "%",
      note: "Distance taken to reach 90% throttle from initial application."
    }
  ];

  return (
    <>
      <tr style={{ borderBottom: "1px solid var(--border)", background: "rgba(255,255,255,0.01)" }}>
        <td rowSpan={metrics.length + 1} style={{ padding: "16px", fontWeight: 700, verticalAlign: "top", borderRight: "1px solid var(--border)", width: "200px" }}>
          <div style={{ fontSize: "16px" }}>{corner.corner}</div>
        </td>
      </tr>
      {metrics.map(m => {
        const valA = corner.lapA ? corner.lapA[m.key] : null;
        const valB = corner.lapB ? corner.lapB[m.key] : null;
        const peakBrakeA = corner.lapA ? corner.lapA.peakBrake : 0;
        const peakBrakeB = corner.lapB ? corner.lapB.peakBrake : 0;
        
        let delta = 0;
        let displayValA = "-";
        let displayValB = "-";
        let displayDelta = "-";

        const formatVal = (val: any, sidx: 'A'|'B') => {
          if (val === null || val === undefined) return "-";
          
          // Special cases for flat-out or no-braking ovals
          if (m.key === "throttleRollDist" && val === 0) return "Flat out";
          if (m.key.startsWith("brake") && (sidx === 'A' ? peakBrakeA : peakBrakeB) === 0) return "No brake";
          
          return m.format(val);
        };

        if (m.key === "timeLossMs") {
          delta = (corner.timeLossMs || 0); // Keep in ms for getDeltaColor
          displayDelta = m.format!(delta);
          displayValA = "-";
          displayValB = "-";
        } else {
          displayValA = formatVal(valA, 'A');
          displayValB = formatVal(valB, 'B');
          
          if (valA !== null && valA !== undefined && valB !== null && valB !== undefined) {
            delta = valA - valB;
            
            // If both are "Flat out" or "No brake", delta is 0
            if (displayValA === displayValB && (displayValA === "Flat out" || displayValA === "No brake")) {
              displayDelta = "0.0";
            } else {
              const deltaVal = delta;
              if (m.key.toLowerCase().includes("speed")) {
                displayDelta = units.formatDelta(deltaVal * (units.system === "metric" ? 3.6 : 2.23694)).replace('s', '');
              } else if (m.key.toLowerCase().includes("pct")) {
                displayDelta = (deltaVal > 0 ? "+" : "") + (deltaVal * 100).toFixed(1) + "%";
              } else {
                displayDelta = (deltaVal > 0 ? "+" : "") + deltaVal.toFixed(1);
              }
            }
          }
        }
        
        return (
          <tr key={m.key} style={{ borderBottom: "1px solid var(--border)" }}>
            <td 
              style={{ padding: "8px 16px", color: "var(--text2)", cursor: "help" }}
              title={m.note}
            >
              {m.label}
            </td>
            <td style={{ padding: "8px 16px", fontVariantNumeric: "tabular-nums" }}>
              {displayValA}
            </td>
            <td style={{ padding: "8px 16px", fontVariantNumeric: "tabular-nums" }}>
              {displayValB}
            </td>
            <td style={{ padding: "8px 16px", fontVariantNumeric: "tabular-nums", color: getDeltaColor(delta, m.key), fontWeight: m.key === "timeLossMs" ? 600 : 400 }}>
              {displayDelta}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function getDeltaColor(delta: number | null, key: string): string {
  if (delta === null || Math.abs(delta) < 0.001) return "inherit";
  
  // For Speed, positive is better (green)
  if (key.toLowerCase().includes("speed")) {
    return delta > 0 ? "#4ade80" : "#f87171";
  }
  
  // For Time Loss, positive is worse (red)
  if (key === "timeLossMs") {
    return delta > 0 ? "#f87171" : "#4ade80";
  }

  // For Temps, positive is usually worse (red) - simplified
  if (key.toLowerCase().includes("temp")) {
    return delta > 0 ? "#f87171" : "#4ade80";
  }
  
  return "inherit";
}

